/**
 * The date-range math behind the schedule view's week navigation.
 *
 * `listAppointmentsQuerySchema` (`@emr/shared`) requires both `from` and `to` as UTC instants and
 * refuses an open-ended query on purpose (`shared/src/schedulingRequests.ts`'s own comment: "a
 * client cannot accidentally ask for a provider's entire history in one call"). This module is the
 * one place that turns "the week containing this date" into that `[from, to)` pair, so the
 * pagination/prev-next logic is pure and testable without a component or a network call.
 */

/** Midnight, local time, on the Monday of the week containing `date`. Monday-based to match the
 * front-desk convention of a business week, not `Date.getDay()`'s Sunday-first numbering. */
export function startOfWeek(date: Date): Date {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // getDay(): 0 = Sunday .. 6 = Saturday. Days to subtract to reach Monday: Sunday -> 6, Monday -> 0,
  // Tuesday -> 1, etc.
  const day = start.getDay();
  const daysSinceMonday = day === 0 ? 6 : day - 1;
  start.setDate(start.getDate() - daysSinceMonday);
  return start;
}

/** Calendar-day addition via `setDate`, not a fixed millisecond offset — a fixed 24h-per-day offset
 * drifts the wall-clock hour across a DST transition, which a week-wide range crosses at least twice
 * a year. `setDate` lets the JS `Date` engine apply the DST adjustment itself. */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export interface WeekRange {
  /** Monday, 00:00 local. */
  readonly from: Date;
  /** The following Monday, 00:00 local — half-open, matching `hasOverlap`'s own `[start, end)`
   * convention in `server/src/scheduling/service.ts`. */
  readonly to: Date;
}

/** The 7-day, Monday-to-Monday range containing `anchor`. */
export function weekContaining(anchor: Date): WeekRange {
  const from = startOfWeek(anchor);
  return { from, to: addDays(from, 7) };
}

/** Shift a week range by whole weeks — `+1` for next week, `-1` for previous. */
export function shiftWeek(range: WeekRange, weeks: number): WeekRange {
  const from = addDays(range.from, weeks * 7);
  return { from, to: addDays(from, 7) };
}

/** The `from`/`to` query params `listAppointments` sends — always `Z`-suffixed, matching
 * `schedulingRequests.ts`'s `instantSchema`. */
export function weekRangeToQuery(range: WeekRange): { readonly from: string; readonly to: string } {
  return { from: range.from.toISOString(), to: range.to.toISOString() };
}

/** Every local calendar day in the range, as `Date`s at local midnight — what the schedule view
 * groups appointments under, one heading per day even when a day has no appointments. */
export function daysInWeek(range: WeekRange): readonly Date[] {
  return Array.from({ length: 7 }, (_, index) => addDays(range.from, index));
}
